import { useEffect, useState } from 'react';

import type { ScriptElement } from '../../schema';
import { normalizeBoardText } from '../boardLayout';
import { textEffectAttrs, textEffectSvgAttrs } from '../text-effects';
import { ChalkWriter } from './ChalkWriter';
import { chalkFontUrl, loadChalkFace } from './chalkAssets';
import { HandwritingText } from './HandwritingText';

/**
 * Chalk-themed text renderer. Element content is an HTML string authored in the
 * admin's Tiptap editor. Styling (font size, weight, color, alignment) is read
 * straight from the authored `style` so the text renders exactly as on the
 * backend; authored line breaks are preserved by `normalizeBoardText`.
 *
 * While `writing` is true the text writes itself (pencil + glyph outlines) via
 * {@link ChalkWriter}. Once settled it STAYS in the handwritten letterforms
 * (static fully-inked glyphs) when chalk fonts are available — the text never
 * swaps back to a normal font after the animation. Hosts without chalk fonts
 * fall back to plain HTML.
 */
export function TextElement({
  element,
  fill,
  writing,
  writeMs,
  onWritingDone,
  paused,
}: {
  element: ScriptElement;
  fill?: boolean;
  writing?: boolean;
  /** Target duration (ms) for the write-on, synced to the teacher's pace. */
  writeMs?: number;
  /** Fired when the character stream finishes (writing → static hand-off). */
  onWritingDone?: () => void;
  /** Freeze the character stream in place (mirrors the player's pause). */
  paused?: boolean;
}) {
  const raw = typeof element.content === 'string' ? element.content : '';
  const styledHtml = normalizeBoardText(raw);

  // Handwriting is opt-out (`handwritten: false`); absent = ON. The flag only
  // controls the write-on ANIMATION — the board typeface is the same either
  // way: unticked text renders as plain HTML, but still in the chalk font
  // unless the author explicitly picked a family.
  const handwritten = element.handwritten !== false;

  // Render at the authored size — the positioned box was sized for it, so the
  // text must fill it to match the backend/editor exactly (no down-scaling).
  const authoredFontSize =
    (element.style?.fontSize as number | undefined) ?? 16;
  const weight = (element.style?.fontWeight as number | undefined) ?? 400;
  const authoredFamily =
    (element.style?.fontFamily as string | undefined)?.trim() || undefined;

  // Default (no authored family) → the host's injected chalk font, registered
  // as a CSS face so plain HTML text uses the exact same TTF the handwriting
  // glyphs come from. 'Kalam' rides the fallback stack for hosts that also
  // declare it in CSS (the admin does); generic cursive is the last resort.
  const [chalkFamily, setChalkFamily] = useState<string | null>(null);
  useEffect(() => {
    if (authoredFamily) return undefined;
    const url = chalkFontUrl(undefined, weight);
    if (!url) return undefined;
    let on = true;
    loadChalkFace(url)
      .then((fam) => {
        if (on && fam) setChalkFamily(fam);
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [authoredFamily, weight]);
  const boardFamily = chalkFamily ? `"${chalkFamily}", Kalam, cursive` : 'Kalam, cursive';

  // NOTE: `base` keeps the AUTHORED family (may be undefined) — the chalk
  // paths below pass it to the host's font port, which keys on authored names.
  // The board-font default is applied only on the plain-HTML render.
  const base: React.CSSProperties = {
    color: (element.style?.color as string | undefined) ?? '#2f2a22',
    fontSize: authoredFontSize,
    fontWeight: weight,
    fontFamily: authoredFamily,
    textAlign:
      (element.style?.textAlign as React.CSSProperties['textAlign']) ?? 'left',
    width: '100%',
    ...(fill ? { height: '100%' } : null),
  };

  // Accent effects on the SVG handwriting path: highlight becomes one block
  // bar behind the writing; long shadow becomes a drop-shadow filter chain
  // (text-shadow doesn't reach SVG glyph fills).
  const svgEffects = textEffectSvgAttrs(element.style, authoredFontSize);
  const wrapAccents = (node: React.ReactNode) =>
    svgEffects ? (
      <div
        {...(svgEffects['data-text-hl'] != null ? { 'data-text-hl': '' } : {})}
        style={svgEffects.style}
      >
        {node}
      </div>
    ) : (
      node
    );

  if (writing && handwritten) {
    return wrapAccents(
      <ChalkWriter
        html={styledHtml}
        className="chalk-text whitespace-normal break-words"
        style={base}
        onDone={onWritingDone}
        paused={paused}
        speed={element.handwritingSpeed ?? 0.3}
        targetMs={writeMs}
      />,
    );
  }

  // Settled state: keep the handwritten letterforms (fully inked, no animation)
  // so finishing a write never swaps the text back to a normal font. Same glyph
  // layout as the write itself → pixel-identical, no reflow on hand-off.
  if (handwritten && chalkFontUrl(authoredFamily, weight) != null) {
    return wrapAccents(
      <HandwritingText
        html={styledHtml}
        className="chalk-text whitespace-normal break-words"
        style={base}
        staticRender
        showPen={false}
      />,
    );
  }

  // Plain HTML render (handwriting unticked, or no chalk glyphs available):
  // still the board typeface unless the author explicitly picked a family.
  // Accent effects (marker highlight / long shadow) apply here — they're a
  // plain-text treatment, not part of the chalk-glyph pipeline.
  const effects = textEffectAttrs(element.style, authoredFontSize);
  return (
    <div
      className="chalk-text whitespace-normal break-words"
      {...(effects['data-text-hl'] != null ? { 'data-text-hl': '' } : {})}
      style={{ ...base, fontFamily: authoredFamily ?? boardFamily, ...effects.style }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: styledHtml }}
    />
  );
}
